import { useState, useMemo } from 'react';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { McpIcon } from '@/components/atoms/Icons/McpIcon';
import useMcpServers from '@/components/pages/MCPs/hooks/useMcpServers';
import { selectAccountSlug } from '@/redux/session/selectors';
import type { ToolInvocation } from '@/shared/types/tryIt';

import { ToolInvocationRow } from './ToolInvocationRow';

type ToolCallsSectionProps = { toolInvocations: ToolInvocation[]; brainId: string };

type EnrichedInvocation = {
  invocation: ToolInvocation;
  serverName: string;
  isSynthetic: boolean;
  key: string;
};

export const ToolCallsSection = ({ toolInvocations, brainId }: ToolCallsSectionProps) => {
  const { t, i18n } = useTranslation();
  const slug = useSelector(selectAccountSlug);
  const { mcpServers } = useMcpServers(brainId);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const enrichedInvocations = useMemo((): EnrichedInvocation[] => {
    return toolInvocations.map((inv, index) => {
      const syntheticKey = `mcp_tools.${inv.mcp_id}`;
      const isSynthetic = i18n.exists(syntheticKey);
      const serverName =
        mcpServers.find((s) => s.mcp_id === inv.mcp_id)?.name ??
        (isSynthetic ? t(syntheticKey) : inv.mcp_id);

      return {
        invocation: inv,
        serverName,
        isSynthetic,
        key: `${inv.mcp_id}-${inv.name}-${index}`,
      };
    });
  }, [toolInvocations, mcpServers, t, i18n]);

  const toggleRow = (key: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const totalToolCount = toolInvocations.length;

  return (
    <div className="mt-2 flex flex-col">
      <div className="flex items-center gap-4">
        <McpIcon size={16} className="text-foreground-muted" />
        <span className="text-sm text-foreground-muted">
          {t('try_it.tool_calls_made', { count: totalToolCount })}
        </span>
      </div>
      <div className="mx-2 my-1.5 min-h-3.5 border-l border-stroke">
        <div className="flex flex-col gap-1 pb-2 pl-6">
          {enrichedInvocations.map((item) => (
            <ToolInvocationRow
              key={item.key}
              invocation={item.invocation}
              serverName={item.serverName}
              serverLink={`/${slug}/ai-agents/${brainId}/workflows/mcps`}
              isSynthetic={item.isSynthetic}
              isExpanded={expandedRows.has(item.key)}
              onToggle={() => toggleRow(item.key)}
            />
          ))}
        </div>
      </div>
    </div>
  );
};
